import { BrInput, BrSelect } from '@govbr-ds/react-components';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import { IVendaDireta } from '../../../../../../../../../interfaces/IBaseDeCalculo';
import styles from './operacaoVendaForm.module.scss';
import { validarDocumento } from '../../../../../../../../../utils/validarDocumento';
import { formatarCNPJOrCpf, unmaskSelectCpfCnpj } from '../../../../../../../../../utils/formatCNPJOrCPF';
import { ETipoOperacaoVendaDireta, TipoOperacaoVendaDiretaDetalhe } from 'enums/ETipoOperacaoVendaDireta';

interface ITitularArrendatarioOperacaoVendaFormProps {
  setVendaDireta: React.Dispatch<React.SetStateAction<IVendaDireta | undefined>>;
  setIsFormularioValido: React.Dispatch<React.SetStateAction<boolean>>;
  setMensagemErro: React.Dispatch<React.SetStateAction<string[]>>;
  vendaDiretaEdicao?: IVendaDireta;
}

const OperacaoVendaForm: React.FC<ITitularArrendatarioOperacaoVendaFormProps> = ({
  setVendaDireta,
  setIsFormularioValido,
  setMensagemErro,
  vendaDiretaEdicao,
}) => {
  const [novaVenda, setNovaVenda] = useState<IVendaDireta>();

  useEffect(() => {
    if (vendaDiretaEdicao) {
      setNovaVenda(vendaDiretaEdicao);
    }
  }, [vendaDiretaEdicao]);

  useEffect(() => {
    const validarFormulario = () => {
      const mensagensErros: string[] = [];

      if (novaVenda?.tipoOperacao === undefined) {
        setIsFormularioValido(false);
        mensagensErros.push('Campo Operação não preenchido');
      }

      if (novaVenda?.tipoOperacao !== TipoOperacaoVendaDiretaDetalhe[ETipoOperacaoVendaDireta.EXPORTACAO_DIRETA].nome) {
        // Validação do campo de CPF/CNPJ apenas para operações que não sejam Exportação Direta
        if (novaVenda?.cpfCnpj === '') {
          setIsFormularioValido(false);
          mensagensErros.push('Campo CPF/CNPJ não preenchido');
        }

        if (!validarDocumento(novaVenda?.cpfCnpj || '')) {
          setIsFormularioValido(false);
          mensagensErros.push('CPF/CNPJ inválido');
        }
      }

      if (novaVenda?.nomeRazaoSocial === '') {
        setIsFormularioValido(false);
        mensagensErros.push('Campo Nome/Razão Social não preenchido');
      }

      if (mensagensErros.length > 0) {
        setMensagemErro(mensagensErros);
        setIsFormularioValido(false);
        return;
      }
      setVendaDireta(novaVenda);
      setIsFormularioValido(true);
    };

    validarFormulario();
  }, [novaVenda, setIsFormularioValido, setMensagemErro, setVendaDireta]);

  const getComplementoLabel = (campo: string) => {
    if (
      campo === 'CPF/CNPJ' &&
      novaVenda?.tipoOperacao === TipoOperacaoVendaDiretaDetalhe[ETipoOperacaoVendaDireta.EXPORTACAO_DIRETA].nome
    ) {
      return '(opcional)';
    }
    return '';
  };

  const onChangeNomeRazaoSocial = (nomeRazaoSocial: string) => {
    setNovaVenda({
      ...novaVenda,
      id: novaVenda?.id || 0,
      nomeRazaoSocial: nomeRazaoSocial,
      cpfCnpj: novaVenda?.cpfCnpj || null,
      quantidadeTotal: novaVenda?.quantidadeTotal || 0,
      valorTotal: novaVenda?.valorTotal || 0,
      codigosNCM: novaVenda?.codigosNCM || [],
      tipoOperacao: novaVenda?.tipoOperacao || ETipoOperacaoVendaDireta.VENDA_MERCADO_INTERNO,
    });
  };

  const onChangeCpfCnpj = (cpfcnpj: string) => {
    const cpfCnpjSemMascara = cpfcnpj.replace(/[.\-/\s]/g, '');

    setNovaVenda({
      ...novaVenda,
      id: novaVenda?.id || 0,
      cpfCnpj: cpfCnpjSemMascara,
      nomeRazaoSocial: novaVenda?.nomeRazaoSocial || '',
      quantidadeTotal: novaVenda?.quantidadeTotal || 0,
      valorTotal: novaVenda?.valorTotal || 0,
      codigosNCM: novaVenda?.codigosNCM || [],
      tipoOperacao: novaVenda?.tipoOperacao || ETipoOperacaoVendaDireta.VENDA_MERCADO_INTERNO,
    });
  };

  const onChangeTipoOperacao = (value: ETipoOperacaoVendaDireta) => {
    setNovaVenda({
      ...novaVenda,
      id: novaVenda?.id || 0,
      tipoOperacao: value,
      nomeRazaoSocial: novaVenda?.nomeRazaoSocial || '',
      cpfCnpj: novaVenda?.cpfCnpj || null,
      quantidadeTotal: novaVenda?.quantidadeTotal || 0,
      valorTotal: novaVenda?.valorTotal || 0,
      codigosNCM: novaVenda?.codigosNCM || [],
    });
  };

  const tipoOperacao = novaVenda?.tipoOperacao || ETipoOperacaoVendaDireta.VENDA_MERCADO_INTERNO;
  return (
    <div className={styles.container}>
      <Row className={styles.row}>
        <Col>
          <BrSelect
            className={styles.select}
            label='Operação'
            onChange={(value: ETipoOperacaoVendaDireta) => onChangeTipoOperacao(value)}
            options={Object.keys(ETipoOperacaoVendaDireta).map((key) => ({
              label: ETipoOperacaoVendaDireta[key as keyof typeof ETipoOperacaoVendaDireta],
              value: key,
            }))}
            value={tipoOperacao}
          />
        </Col>
      </Row>
      <Row className={styles.row}>
        <Col sm={4}>
          <BrInput
            label={'CPF/CNPJ' + ' ' + getComplementoLabel('CPF/CNPJ')}
            value={formatarCNPJOrCpf(novaVenda?.cpfCnpj || '')}
            onChange={(e) => onChangeCpfCnpj(unmaskSelectCpfCnpj(e.target.value).slice(0, 14))}
          />
        </Col>
        <Col sm={8}>
          <BrInput
            label='Nome/Razão Social'
            value={novaVenda?.nomeRazaoSocial}
            onChange={(e) => onChangeNomeRazaoSocial(e.target.value)}
          />
        </Col>
      </Row>
    </div>
  );
};

export default OperacaoVendaForm;
